// Archetipi EA dei Pro e aggregazioni per la sezione Rosa > Archetipi.
//
// EA manda solo il numero (`archetypeid`), mai il nome. La mappa e' stata
// ricavata incrociando gli archetipi con il ruolo in rosa su tutte le
// squadre e confermata dai giocatori del Ca De Rissi (12 Finisher,
// 2 Sweeper Keeper). Il 5 cambia significato: in FC 26 era Engine
// (difensore), in FC 27 EA l'ha tolto e al suo posto c'e' Disruptor
// (mediano). I nomi restano quelli del gioco, non si traducono.

export type ArchetypeDept = "gk" | "def" | "mid" | "att";

type ArchetypeInfo = { name: string; dept: ArchetypeDept };

const BASE: Record<number, ArchetypeInfo> = {
  1: { name: "Shot Stopper", dept: "gk" },
  2: { name: "Sweeper Keeper", dept: "gk" },
  3: { name: "Progressor", dept: "def" },
  4: { name: "Boss", dept: "def" },
  5: { name: "Disruptor", dept: "mid" },
  6: { name: "Marauder", dept: "def" },
  7: { name: "Recycler", dept: "mid" },
  8: { name: "Maestro", dept: "mid" },
  9: { name: "Creator", dept: "mid" },
  10: { name: "Spark", dept: "mid" },
  11: { name: "Magician", dept: "att" },
  12: { name: "Finisher", dept: "att" },
  13: { name: "Target", dept: "att" },
};

const BY_GAME: Record<string, Record<number, ArchetypeInfo>> = {
  fc26: { ...BASE, 5: { name: "Engine", dept: "def" } },
};

export function archetypeInfo(id: number, game: string): ArchetypeInfo | null {
  return BY_GAME[game]?.[id] ?? BASE[id] ?? null;
}

export function archetypeName(id: number, game: string): string {
  return archetypeInfo(id, game)?.name ?? `#${id}`;
}

export const DEPT_ORDER: ArchetypeDept[] = ["gk", "def", "mid", "att"];

// Stessi colori per reparto del cerchio ruolo nel dettaglio partita.
export const DEPT_CLASS: Record<ArchetypeDept | "other", string> = {
  gk: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  def: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
  mid: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  att: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  other: "bg-muted text-muted-foreground",
};

export function deptOf(id: number, game: string): ArchetypeDept | "other" {
  return archetypeInfo(id, game)?.dept ?? "other";
}

export function sortArchetypes(ids: number[], game: string): number[] {
  const rank = (id: number) => {
    const d = deptOf(id, game);
    return (d === "other" ? 9 : DEPT_ORDER.indexOf(d)) * 100 + id;
  };
  return [...ids].sort((a, b) => rank(a) - rank(b));
}

// --- Righe della RPC team_archetype_stats ---------------------------------

export type OursRow = {
  game: string;
  playername: string;
  archetype: number;
  player_id: string | null;
  display_name: string | null;
  role_code: string | null;
  main_pos: string | null;
  matches: number;
  wins: number;
  draws: number;
  losses: number;
  avg_rating: number | null;
  goals: number;
  assists: number;
  shots: number;
  passes_made: number;
  pass_attempts: number;
  tackles_made: number;
  tackle_attempts: number;
  saves: number;
  goals_conceded: number;
  mom: number;
  last_played: string | null;
};

export type OppRow = {
  game: string;
  archetype: number;
  appearances: number;
  matches: number;
  wins: number;
  draws: number;
  losses: number;
  avg_rating: number | null;
  goals: number;
  assists: number;
};

export type ArchetypeStatsPayload = { ours: OursRow[]; opponents: OppRow[] };

// Somma di piu' righe (stesso archetipo, giocatori diversi o viceversa). Il
// voto medio e' pesato sulle partite.
export type Totals = {
  matches: number;
  wins: number;
  draws: number;
  losses: number;
  ratingSum: number;
  ratingMatches: number;
  goals: number;
  assists: number;
  passes_made: number;
  pass_attempts: number;
  tackles_made: number;
  saves: number;
  goals_conceded: number;
};

export function emptyTotals(): Totals {
  return {
    matches: 0, wins: 0, draws: 0, losses: 0, ratingSum: 0, ratingMatches: 0,
    goals: 0, assists: 0, passes_made: 0, pass_attempts: 0, tackles_made: 0, saves: 0, goals_conceded: 0,
  };
}

export function addRow(t: Totals, r: OursRow): Totals {
  t.matches += r.matches;
  t.wins += r.wins;
  t.draws += r.draws;
  t.losses += r.losses;
  if (r.avg_rating != null) {
    t.ratingSum += r.avg_rating * r.matches;
    t.ratingMatches += r.matches;
  }
  t.goals += r.goals;
  t.assists += r.assists;
  t.passes_made += r.passes_made;
  t.pass_attempts += r.pass_attempts;
  t.tackles_made += r.tackles_made;
  t.saves += r.saves;
  t.goals_conceded += r.goals_conceded;
  return t;
}

export const avgRating = (t: Totals) => (t.ratingMatches > 0 ? t.ratingSum / t.ratingMatches : null);
export const perMatch = (value: number, matches: number) => (matches > 0 ? value / matches : 0);
export const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : null);

// Sotto queste soglie un confronto "rende meglio con" o "migliore del
// reparto" non e' affidabile e non viene evidenziato.
export const MIN_MATCHES_PER_ARCHETYPE = 5;
export const MIN_MATCHES_FOR_BEST = 5;
